import { useCallback, useEffect, useMemo, useState } from 'react';
import { InterviewMode, SessionStatus, type InterviewSession as BaseInterviewSession } from '@shared/types.ts';
import { buildGuideSummary, getTemplateGuide } from '../lib/guideTemplate';
import {
  type AssistantMode,
  type CustomConfig,
  abortInterviewStart,
  endInterview,
  getInterviewerOverview,
  getSession,
  restartInterview,
  startChatInterview,
  startInterview,
  type InterviewerOverview,
} from '../services/api';
import type {
  ConnectionStatus,
  MicrophonePermission,
  UseVapiReturn,
} from '../hooks/useVapi';
import { AssistantConfigEditor, type AssistantConfig } from './AssistantConfigEditor';
import { InterviewView, type InterviewViewState } from './interview/InterviewView';
import { ModeSelector } from './ModeSelector';
import { ChatInterview } from './chat/ChatInterview';
import { Skeleton } from './feedback';

interface InterviewSessionProps {
  sessionId: string;
  vapi: UseVapiReturn;
  onCallIdChange?: (callId: string | null) => void;
}

type InterviewSessionDetails = BaseInterviewSession;

type TranscriptItems = UseVapiReturn['transcript'];
type TranscriptItem = TranscriptItems[number];

const statusStyles: Record<ConnectionStatus, { label: string; className: string }> = {
  idle: {
    label: 'Idle',
    className: 'border-border bg-muted text-foreground',
  },
  connecting: {
    label: 'Connecting',
    className: 'border-kaiva-coral/40 bg-kaiva-coral/10 text-kaiva-coral',
  },
  connected: {
    label: 'Connected',
    className: 'border-kaiva-success/40 bg-kaiva-success/10 text-kaiva-success',
  },
  disconnected: {
    label: 'Disconnected',
    className: 'border-border bg-muted text-muted-foreground',
  },
  error: {
    label: 'Error',
    className: 'border-kaiva-coral/40 bg-kaiva-coral/10 text-kaiva-coral',
  },
};

const microphoneStyles: Record<
  MicrophonePermission,
  { label: string; className: string; help: string }
> = {
  granted: {
    label: 'Microphone Ready',
    className: 'border-kaiva-success/40 bg-kaiva-success/10 text-kaiva-success',
    help: 'Audio input is available.',
  },
  prompt: {
    label: 'Microphone Permission Needed',
    className: 'border-kaiva-warning/40 bg-kaiva-warning/10 text-kaiva-warning',
    help: 'Click Start and allow microphone access when prompted.',
  },
  denied: {
    label: 'Microphone Blocked',
    className: 'border-kaiva-coral/40 bg-kaiva-coral/10 text-kaiva-coral',
    help: 'Enable microphone permission in browser settings, then retry.',
  },
  unsupported: {
    label: 'Microphone Status Unknown',
    className: 'border-border bg-muted text-foreground',
    help: 'Browser does not support microphone permission checks.',
  },
};

function toErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'An unexpected error occurred.';
}

function isNotFoundError(error: unknown): boolean {
  const message = toErrorMessage(error);
  return message.includes('(404)');
}

function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainder = seconds % 60;

  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${remainder.toString().padStart(2, '0')}`;
  }

  return `${minutes.toString().padStart(2, '0')}:${remainder.toString().padStart(2, '0')}`;
}

function formatDisplayDate(value: string | Date | null | undefined): string {
  if (!value) {
    return '---';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '---';
  }

  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getTranscriptSpeakerLabel(entry: TranscriptItem): string {
  if (entry.role === 'assistant') {
    return 'Kaiva';
  }
  if (entry.role === 'user') {
    return 'You';
  }
  return 'System';
}

function buildTranscriptText(
  sessionName: string,
  durationLabel: string,
  entries: TranscriptItems
): string {
  const header = [
    'Kaiva Interview Transcript',
    `Session: ${sessionName}`,
    `Duration: ${durationLabel}`,
    `Generated: ${new Date().toISOString()}`,
    '',
    '---',
    '',
  ].join('\n');

  const body = entries
    .map((entry) => {
      const text = entry.text?.trim();
      if (!text) {
        return '';
      }
      const timestamp = new Date(entry.timestamp ?? new Date().toISOString());
      const time = Number.isNaN(timestamp.getTime())
        ? new Date().toLocaleTimeString()
        : timestamp.toLocaleTimeString();
      return `[${time}] ${getTranscriptSpeakerLabel(entry)}: ${text}`;
    })
    .filter((line) => line.length > 0)
    .join('\n');

  return `${header}${body}`;
}

function formatJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return 'Unable to render this value.';
  }
}

function asAssistantMode(value: unknown): AssistantMode | null {
  if (value === 'dashboard' || value === 'custom') {
    return value;
  }

  return null;
}

const templateGuide = getTemplateGuide();
const templateGuideData = buildGuideSummary(templateGuide);
const templateTopics = templateGuideData.sections.map((section) => section.title);
const DEMO_VAPI_SESSION_ID = '00000000-0000-0000-0000-000000000002';

export function InterviewSession({ sessionId, vapi, onCallIdChange }: InterviewSessionProps) {
  const [session, setSession] = useState<InterviewSessionDetails | null>(null);
  const [assistantMode, setAssistantMode] = useState<AssistantMode>('dashboard');
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [isStarting, setIsStarting] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [isRestarting, setIsRestarting] = useState(false);
  const [isCheckingMicrophone, setIsCheckingMicrophone] = useState(false);
  const [isAgentInfoOpen, setIsAgentInfoOpen] = useState(false);
  const [isLoadingAgentInfo, setIsLoadingAgentInfo] = useState(false);
  const [uiError, setUiError] = useState<string | null>(null);
  const [agentInfoError, setAgentInfoError] = useState<string | null>(null);
  const [agentInfo, setAgentInfo] = useState<InterviewerOverview | null>(null);
  const [startedAt, setStartedAt] = useState<Date | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [customConfig, setCustomConfig] = useState<AssistantConfig | null>(null);
  const [templateMode, setTemplateMode] = useState(false);
  const [templateStatus, setTemplateStatus] = useState<SessionStatus>(SessionStatus.PENDING);
  const [templatePaused, setTemplatePaused] = useState(false);

  const {
    status,
    transcript,
    error: vapiError,
    micPermission,
    requestMicrophoneAccess,
    startCall,
    endCall,
    isMuted,
    setMuted,
  } = vapi;

  const loadSession = useCallback(async () => {
    setIsLoadingSession(true);
    setUiError(null);
    setStartedAt(null);
    setElapsedSeconds(0);

    try {
      const response = await getSession(sessionId);
      const loadedSession = response.session as InterviewSessionDetails;
      const metadata = loadedSession.metadata;
      const metadataMode =
        metadata && typeof metadata === 'object'
          ? asAssistantMode((metadata as Record<string, unknown>).assistantMode)
          : null;

      if (metadataMode) {
        setAssistantMode(metadataMode);
      }

      setSession(loadedSession);
      setTemplateMode(false);
      setTemplatePaused(false);
      onCallIdChange?.(loadedSession.vapiCallId ?? null);

      if (loadedSession.startedAt) {
        const parsedStart = new Date(loadedSession.startedAt);
        if (!Number.isNaN(parsedStart.getTime())) {
          setStartedAt(parsedStart);
          setElapsedSeconds(
            Math.max(0, Math.floor((Date.now() - parsedStart.getTime()) / 1000))
          );
        }
      }
    } catch (error) {
      if (isNotFoundError(error)) {
        setTemplateMode(true);
        setSession(null);
        setStartedAt(null);
        setElapsedSeconds(0);
        setTemplateStatus(SessionStatus.PENDING);
        setTemplatePaused(false);
        setIsAgentInfoOpen(false);
        setAgentInfo(null);
        setAgentInfoError(null);
        setIsLoadingAgentInfo(false);
        onCallIdChange?.(null);
      } else {
        setUiError(toErrorMessage(error));
      }
    } finally {
      setIsLoadingSession(false);
    }
  }, [onCallIdChange, sessionId]);

  useEffect(() => {
    void loadSession();
  }, [loadSession]);

  const shouldRunTimer =
    !!startedAt &&
    (status === 'connected' ||
      (templateMode &&
        templateStatus === SessionStatus.IN_PROGRESS &&
        !templatePaused));

  useEffect(() => {
    if (!shouldRunTimer || !startedAt) {
      return;
    }

    const updateTimer = () => {
      setElapsedSeconds(Math.max(0, Math.floor((Date.now() - startedAt.getTime()) / 1000)));
    };

    updateTimer();
    const timerId = window.setInterval(updateTimer, 1000);

    return () => {
      window.clearInterval(timerId);
    };
  }, [startedAt, shouldRunTimer]);

  const topics = useMemo(
    () => {
      if (templateMode) return templateTopics;
      // Extract topics from structureData sections if available
      const data = session?.structureData;
      if (data && typeof data === 'object' && 'sections' in data && Array.isArray((data as Record<string, unknown>).sections)) {
        const sections = (data as Record<string, unknown>).sections as Array<{ title?: string }>;
        return sections.map((s) => s.title ?? '').filter(Boolean);
      }
      return [];
    },
    [session, templateMode]
  );
  const statusBadge = statusStyles[status];
  const microphoneBadge = microphoneStyles[micPermission];
  const activeErrorMessage = templateMode
    ? uiError
    : uiError ?? (vapiError ? toErrorMessage(vapiError) : null);
  const isTemplateActive = templateMode && templateStatus === SessionStatus.IN_PROGRESS;
  const isActive = templateMode ? isTemplateActive : status === 'connected' || status === 'connecting';
  const canSwitchInterviewer = !isActive && !isStarting && !isEnding;
  const interviewerName = templateMode
    ? 'Kaiva (Template)'
    : agentInfo?.displayName ?? 'Kaiva';
  const interviewerSourceText = templateMode
    ? 'Template mode (voice enabled)'
    : agentInfo?.source === 'vapi-assistant'
      ? 'Using VAPI Dashboard assistant'
      : agentInfo?.source === 'custom-preview'
        ? 'Using custom Kaiva setup from code'
        : assistantMode === 'custom'
          ? 'Using custom Kaiva setup from code'
          : 'Using guide preview from code';

  const loadInterviewerInfo = useCallback(async () => {
    setIsLoadingAgentInfo(true);
    setAgentInfoError(null);
    setAgentInfo(null);

    try {
      const agentSessionId = templateMode ? DEMO_VAPI_SESSION_ID : sessionId;
      const response = await getInterviewerOverview(agentSessionId, assistantMode);
      setAgentInfo(response.interviewer);
    } catch (error) {
      setAgentInfoError(toErrorMessage(error));
    } finally {
      setIsLoadingAgentInfo(false);
    }
  }, [assistantMode, sessionId, templateMode]);

  useEffect(() => {
    void loadInterviewerInfo();
  }, [loadInterviewerInfo]);

  const handleStart = async () => {
    const sessionIdForCall = templateMode ? DEMO_VAPI_SESSION_ID : session?.id;

    if (!sessionIdForCall) {
      return;
    }

    setUiError(null);
    setIsStarting(true);

    let startResponse: Awaited<ReturnType<typeof startInterview>> | null = null;

    try {
      await requestMicrophoneAccess();

      // Convert AssistantConfig to CustomConfig if in custom mode
      const apiCustomConfig: CustomConfig | undefined =
        assistantMode === 'custom' && customConfig
          ? {
              modelProvider: customConfig.modelProvider,
              modelName: customConfig.modelName,
              temperature: customConfig.temperature,
              voiceProvider: customConfig.voiceProvider,
              voiceId: customConfig.voiceId,
              transcriberProvider: customConfig.transcriberProvider,
              transcriberModel: customConfig.transcriberModel,
              transcriberLanguage: customConfig.transcriberLanguage,
              firstMessage: customConfig.firstMessage,
            }
          : undefined;

      const startedResponse = await startInterview(
        sessionIdForCall,
        assistantMode,
        apiCustomConfig
      );
      startResponse = startedResponse;
      onCallIdChange?.(startedResponse.call.id);
      await startCall({
        assistantId: startedResponse.assistantId,
        call: startedResponse.call,
      });

      const startTime = new Date();
      setStartedAt(startTime);
      setElapsedSeconds(0);
      if (templateMode) {
        setTemplatePaused(false);
        setTemplateStatus(SessionStatus.IN_PROGRESS);
      } else {
        setSession((previous) =>
          previous
            ? {
                ...previous,
                status: SessionStatus.IN_PROGRESS,
                vapiCallId:
                  typeof startedResponse.call.id === 'string'
                    ? startedResponse.call.id
                    : previous.vapiCallId,
                startedAt: startTime,
              }
            : previous
        );
      }
    } catch (error) {
      const message = toErrorMessage(error);

      if (startResponse) {
        try {
          await abortInterviewStart(sessionIdForCall, message);
          if (!templateMode) {
            await loadSession();
          }
          onCallIdChange?.(null);
        } catch (abortError) {
          const abortMessage = toErrorMessage(abortError);
          setUiError(`${message} Recovery failed: ${abortMessage}`);
          return;
        }
      }

      setUiError(message);
    } finally {
      setIsStarting(false);
    }
  };

  const handleMicrophoneCheck = async () => {
    setUiError(null);
    setIsCheckingMicrophone(true);

    try {
      await requestMicrophoneAccess();
    } catch (error) {
      setUiError(toErrorMessage(error));
    } finally {
      setIsCheckingMicrophone(false);
    }
  };

  const handleOpenAgentInfo = async () => {
    setIsAgentInfoOpen(true);

    if (!agentInfo && !isLoadingAgentInfo) {
      await loadInterviewerInfo();
    }
  };

  const handleEnd = async () => {
    const sessionIdForCall = templateMode ? DEMO_VAPI_SESSION_ID : session?.id;

    if (!sessionIdForCall) {
      return;
    }

    setUiError(null);
    setIsEnding(true);

    try {
      // Send final transcript entries so the backend can save them for knowledge extraction
      const finalTranscript = transcript
        .filter((t) => t.transcriptType === 'final')
        .map((t) => ({ role: t.role, text: t.text, timestamp: t.timestamp }));
      await endInterview(sessionIdForCall, finalTranscript);
    } catch (error) {
      setUiError(toErrorMessage(error));
    }

    // Always stop the VAPI call and update state, even if the backend request failed.
    // This prevents the timer from running indefinitely when the API errors out.
    endCall();

    const completedAt = new Date();
    const computedDuration = startedAt
      ? Math.max(0, Math.floor((completedAt.getTime() - startedAt.getTime()) / 1000))
      : elapsedSeconds;
    if (templateMode) {
      setElapsedSeconds(computedDuration);
      setTemplateStatus(SessionStatus.COMPLETED);
    } else {
      setSession((previous) =>
        previous
          ? {
              ...previous,
              status: SessionStatus.COMPLETED,
              completedAt,
              duration: computedDuration,
            }
          : previous
      );
    }

    setIsEnding(false);
  };

  const handleRestart = async () => {
    const targetSessionId = templateMode ? DEMO_VAPI_SESSION_ID : session?.id;
    if (!targetSessionId || isRestarting) return;

    setIsRestarting(true);
    setUiError(null);

    try {
      await restartInterview(targetSessionId);
      await loadSession();
    } catch (error) {
      setUiError(toErrorMessage(error));
    } finally {
      setIsRestarting(false);
    }
  };

  const isComplete = templateMode
    ? templateStatus === SessionStatus.COMPLETED
    : session?.status === SessionStatus.COMPLETED;

  const canStart = templateMode
    ? templateStatus === SessionStatus.PENDING &&
      !isStarting &&
      !isCheckingMicrophone &&
      status !== 'connecting' &&
      status !== 'connected' &&
      micPermission !== 'denied'
    : session?.status === SessionStatus.PENDING &&
      !isStarting &&
      !isCheckingMicrophone &&
      status !== 'connecting' &&
      status !== 'connected' &&
      micPermission !== 'denied';

  const canPause = status === 'connected';

  if (isLoadingSession) {
    return (
      <section className="mx-auto w-full max-w-3xl px-6 py-12">
        <div className="rounded-2xl border border-border bg-card p-12 shadow-lg">
          <Skeleton variant="card" />
        </div>
      </section>
    );
  }

  if (!session && !templateMode) {
    return (
      <section className="mx-auto w-full max-w-3xl px-6 py-12">
        <div className="space-y-4 rounded-2xl border border-border bg-card p-12 shadow-lg">
          <div className="rounded-xl border border-kaiva-coral/30 bg-kaiva-coral/10 p-4 text-sm text-kaiva-coral">
            {activeErrorMessage ?? 'Unable to load this interview session.'}
          </div>
          <button
            type="button"
            onClick={() => void loadSession()}
            className="inline-flex items-center rounded-full border border-kaiva-coral/40 bg-card px-4 py-2 text-xs font-semibold text-kaiva-coral transition hover:bg-kaiva-coral/20"
          >
            Retry
          </button>
        </div>
      </section>
    );
  }

  // ── Mode Selection: Show ModeSelector for PENDING sessions (non-template) ──
  // Also re-show for ASYNC_CHAT + PENDING (failed start — user can retry)
  if (
    !templateMode &&
    session &&
    session.status === SessionStatus.PENDING
  ) {
    return (
      <ModeSelector
        sessionName={session.sessionName}
        intervieweeName={session.intervieweeName}
        structureName={session.structureName}
        onStartVoice={() => void handleStart()}
        onStartChat={async (options) => {
          setUiError(null);
          setIsStarting(true);
          try {
            await startChatInterview(session.id, {
              language: options.language,
              coordinatorModel: options.coordinatorModel,
            });
            // Update local session state with the chat mode settings
            setSession((prev) =>
              prev
                ? {
                    ...prev,
                    mode: InterviewMode.ASYNC_CHAT,
                    language: options.language ?? 'de',
                    coordinatorModel: options.coordinatorModel,
                    status: SessionStatus.IN_PROGRESS,
                    startedAt: new Date(),
                  }
                : prev
            );
            setStartedAt(new Date());
          } catch (error) {
            setUiError(toErrorMessage(error));
          } finally {
            setIsStarting(false);
          }
        }}
        isStarting={isStarting}
        error={uiError}
      />
    );
  }

  // ── Chat Interview: Show ChatInterview for ASYNC_CHAT sessions that are IN_PROGRESS ──
  if (
    !templateMode &&
    session &&
    session.mode === InterviewMode.ASYNC_CHAT &&
    session.status === SessionStatus.IN_PROGRESS
  ) {
    return (
      <ChatInterview
        session={session}
        onComplete={() => {
          setSession((prev) =>
            prev
              ? { ...prev, status: SessionStatus.COMPLETED, completedAt: new Date() }
              : prev
          );
        }}
      />
    );
  }

  const viewState: InterviewViewState = templateMode
    ? templateStatus === SessionStatus.COMPLETED
      ? 'complete'
      : templateStatus === SessionStatus.IN_PROGRESS
        ? 'active'
        : 'preparing'
    : session?.status === SessionStatus.COMPLETED
      ? 'complete'
      : session?.status === SessionStatus.IN_PROGRESS || isActive
        ? 'active'
        : 'preparing';

  const sessionName = templateMode
    ? templateGuide.name
    : session?.sessionName ?? 'Interview Session';
  const sessionNumber = templateMode
    ? 'TEMPLATE'
    : session?.id
      ? session.id.slice(0, 8).toUpperCase()
      : '---';
  const scheduledMeeting = !templateMode
    ? ((session?.structureData as Record<string, unknown>)?.scheduledMeeting as {
        scheduledDate?: string;
        scheduledTime?: string;
      } | undefined)
    : undefined;
  const sessionDate = templateMode
    ? formatDisplayDate(templateGuide.created_at)
    : scheduledMeeting?.scheduledDate
      ? `${formatDisplayDate(scheduledMeeting.scheduledDate)}${scheduledMeeting.scheduledTime ? `, ${scheduledMeeting.scheduledTime}` : ''}`
      : formatDisplayDate(session?.startedAt ?? session?.createdAt);
  const sessionTopic = templateMode
    ? templateGuide.name
    : session?.structureName ?? 'Handover interview';
  const sessionDuration = session?.duration;
  const durationSeconds =
    !templateMode && typeof sessionDuration === 'number' && Number.isFinite(sessionDuration)
      ? sessionDuration
      : elapsedSeconds;
  const elapsedLabel = viewState === 'preparing' ? '00:00' : formatDuration(durationSeconds);
  const customConfigPanel =
    assistantMode === 'custom' ? (
      <AssistantConfigEditor onChange={setCustomConfig} disabled={isActive || isStarting} />
    ) : null;

  const handlePauseToggle = () => {
    if (status === 'connected') {
      setMuted(!isMuted);
      return;
    }

    if (templateMode) {
      setTemplatePaused((previous) => !previous);
    }
  };

  const handleDownloadTranscript = () => {
    const text = buildTranscriptText(sessionName, elapsedLabel, transcript);
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    const safeSessionName = sessionName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    const fallbackName = safeSessionName || 'session';

    anchor.href = url;
    anchor.download = `${fallbackName}-transcript.txt`;
    anchor.click();

    URL.revokeObjectURL(url);
  };

  return (
    <>
      <InterviewView
        state={viewState}
        sessionName={sessionName}
        sessionNumber={sessionNumber}
        sessionDate={sessionDate}
        sessionTopic={sessionTopic}
        sessionDescription={
          templateMode
            ? templateGuide.description
            : (session?.structureData as Record<string, unknown>)?.description as string | undefined
        }
        participantName={
          templateMode ? 'Interviewee' : session?.intervieweeName ?? 'Unknown user'
        }
        participantRole={
          templateMode ? 'Template guide preview' : session?.intervieweeEmail ?? ''
        }
        interviewerName={interviewerName}
        interviewerSource={interviewerSourceText}
        statusBadge={statusBadge}
        microphoneBadge={microphoneBadge}
        topics={topics}
        transcript={templateMode ? [] : transcript}
        elapsedLabel={elapsedLabel}
        isPaused={templateMode ? (status === 'connected' ? isMuted : templatePaused) : isMuted}
        isStarting={isStarting}
        isEnding={isEnding}
        canStart={canStart}
        canPause={canPause}
        canEnd={templateMode ? isTemplateActive || status === 'connected' : isActive || session?.status === SessionStatus.IN_PROGRESS}
        onStart={handleStart}
        onPauseToggle={canPause || (templateMode && templateStatus === SessionStatus.IN_PROGRESS) ? handlePauseToggle : undefined}
        onEnd={handleEnd}
        onTestMicrophone={handleMicrophoneCheck}
        isCheckingMicrophone={isCheckingMicrophone}
        assistantMode={assistantMode}
        canChangeAssistantMode={canSwitchInterviewer}
        onChangeAssistantMode={setAssistantMode}
        onOpenAgentInfo={() => void handleOpenAgentInfo()}
        customConfig={customConfigPanel}
        onDownload={templateMode ? undefined : handleDownloadTranscript}
        onRestart={isComplete ? handleRestart : undefined}
        isRestarting={isRestarting}
        templateMode={templateMode}
        templateNotice={
          templateMode
            ? 'Template mode is active. Showing an offline guide preview while the admin session is unavailable. Voice agent controls are still available.'
            : undefined
        }
        guideSummary={templateMode ? templateGuideData.summary : undefined}
        guideSections={templateMode ? templateGuideData.sections : undefined}
        guideContext={templateMode ? templateGuide.designer_context : undefined}
        error={activeErrorMessage}
      />

      {isAgentInfoOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-foreground/30 px-4 py-8">
          <section className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
            <header className="flex items-center justify-between border-b border-border px-6 py-4">
              <div>
                <h2 className="text-base font-semibold text-foreground">
                  Voice Agent Overview
                </h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  Runtime snapshot for this interview session.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsAgentInfoOpen(false)}
                className="rounded-md border border-border px-3 py-1 text-xs font-semibold text-foreground transition hover:bg-muted"
              >
                Close
              </button>
            </header>

            <div className="max-h-[calc(90vh-72px)] space-y-4 overflow-y-auto p-6">
              {isLoadingAgentInfo ? (
                <p className="rounded-lg border border-border bg-muted p-3 text-sm text-muted-foreground">
                  Loading voice agent details...
                </p>
              ) : agentInfoError ? (
                <p className="rounded-lg border border-kaiva-coral/30 bg-kaiva-coral/10 p-3 text-sm text-kaiva-coral">
                  {agentInfoError}
                </p>
              ) : !agentInfo ? (
                <p className="rounded-lg border border-border bg-muted p-3 text-sm text-muted-foreground">
                  No voice agent details available.
                </p>
              ) : (
                <>
                  <div className="grid gap-3 md:grid-cols-2">
                    <article className="rounded-lg border border-border bg-muted p-3 text-sm">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Assistant
                      </p>
                      <p className="mt-1 font-semibold text-foreground">
                        {agentInfo.displayName}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Source: {agentInfo.source}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Mode: {agentInfo.mode}
                      </p>
                      <p className="mt-1 break-all text-xs text-muted-foreground">
                        Assistant ID: {agentInfo.assistantId ?? 'n/a'}
                      </p>
                    </article>

                    <article className="rounded-lg border border-border bg-muted p-3 text-sm">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Input
                      </p>
                      <p className="mt-1 text-foreground">Type: {agentInfo.input.type}</p>
                      <p className="mt-1 text-foreground">
                        Language: {agentInfo.input.language ?? 'n/a'}
                      </p>
                    </article>

                    <article className="rounded-lg border border-border bg-muted p-3 text-sm">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Model
                      </p>
                      <p className="mt-1 text-foreground">
                        Provider: {agentInfo.model.provider ?? 'n/a'}
                      </p>
                      <p className="mt-1 text-foreground">
                        Model: {agentInfo.model.model ?? 'n/a'}
                      </p>
                      <p className="mt-1 text-foreground">
                        Temperature: {agentInfo.model.temperature ?? 'n/a'}
                      </p>
                    </article>

                    <article className="rounded-lg border border-border bg-muted p-3 text-sm">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Voice + Transcriber
                      </p>
                      <p className="mt-1 text-foreground">
                        Voice: {agentInfo.voice.provider ?? 'n/a'} /{' '}
                        {agentInfo.voice.voiceId ?? 'n/a'}
                      </p>
                      <p className="mt-1 text-foreground">
                        Transcriber: {agentInfo.transcriber.provider ?? 'n/a'} /{' '}
                        {agentInfo.transcriber.model ?? 'n/a'}
                      </p>
                    </article>
                  </div>

                  <article className="rounded-lg border border-border bg-card p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      First Message
                    </p>
                    <p className="mt-2 text-sm text-foreground">
                      {agentInfo.firstMessage ?? 'n/a'}
                    </p>
                  </article>

                  <article className="rounded-lg border border-border bg-card p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      System Prompt
                    </p>
                    <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap rounded-md bg-muted p-3 text-xs leading-relaxed text-foreground">
                      {agentInfo.systemPrompt || 'No system prompt available.'}
                    </pre>
                  </article>

                  <article className="rounded-lg border border-border bg-card p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Settings (Accessible)
                    </p>
                    <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded-md bg-muted p-3 text-xs leading-relaxed text-foreground">
                      {formatJson(agentInfo.settings)}
                    </pre>
                  </article>

                  <details className="rounded-lg border border-border bg-card p-3">
                    <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Raw Assistant Payload (Advanced)
                    </summary>
                    <pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap rounded-md bg-muted p-3 text-xs leading-relaxed text-foreground">
                      {formatJson(agentInfo.raw ?? null)}
                    </pre>
                  </details>
                </>
              )}
            </div>
          </section>
        </div>
      )}
    </>
  );
}

export default InterviewSession;
